import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/redux/store";

interface Call {
  id: string;
  visitor_id: string;
  sales_rep_id: string;
  widget_id: string;
  call_type: "video" | "voice";
  status: "initiated" | "ringing" | "ongoing" | "completed" | "cancelled" | "missed";
  daily_room_url?: string;
  daily_room_name?: string;
  started_at?: string;
  ended_at?: string;
  duration?: number;
  meeting_booked: boolean;
  notes?: string;
  created_at: string;
  room?: {
    url?: string;
    name?: string;
  };
  salesRepToken?: string;
  visitor?: {
    id: string;
    page_url: string;
    metadata?: Record<string, any>;
  };
}

interface CallsState {
  calls: Call[];
  activeCall: Call | null;
  activeCallRoom: {
    url: string;
    name?: string;
  } | null;
  activeCallToken: string | null;
  ongoingCalls: Call[];
  isLoading: boolean;
  error: string | null;
}

const initialState: CallsState = {
  calls: [],
  activeCall: null,
  activeCallRoom: null,
  activeCallToken: null,
  ongoingCalls: [],
  isLoading: false,
  error: null,
};

const callsSlice = createSlice({
  name: "calls",
  initialState,
  reducers: {
    setCalls: (state, action: PayloadAction<Call[]>) => {
      state.calls = action.payload;
      state.ongoingCalls = action.payload.filter((c) => c.status === "ongoing");
    },
    addCall: (state, action: PayloadAction<Call>) => {
      const exists = state.calls.find((c) => c.id === action.payload.id);
      if (!exists) {
        state.calls.unshift(action.payload);
        if (action.payload.status === "ongoing") {
          // Check if already in ongoingCalls to prevent duplicates
          const ongoingExists = state.ongoingCalls.find((c) => c.id === action.payload.id);
          if (!ongoingExists) {
            state.ongoingCalls.unshift(action.payload);
          }
        }
      } else {
        // Update existing call
        const index = state.calls.findIndex((c) => c.id === action.payload.id);
        if (index !== -1) {
          state.calls[index] = action.payload;
        }
        // Update ongoingCalls accordingly
        const ongoingIndex = state.ongoingCalls.findIndex((c) => c.id === action.payload.id);
        if (action.payload.status === "ongoing" && ongoingIndex === -1) {
          state.ongoingCalls.unshift(action.payload);
        } else if (action.payload.status !== "ongoing" && ongoingIndex !== -1) {
          state.ongoingCalls.splice(ongoingIndex, 1);
        } else if (action.payload.status === "ongoing" && ongoingIndex !== -1) {
          state.ongoingCalls[ongoingIndex] = action.payload;
        }
      }
    },
    updateCall: (state, action: PayloadAction<Call>) => {
      const index = state.calls.findIndex((c) => c.id === action.payload.id);
      if (index !== -1) {
        // Merge with existing call to preserve fields not in the update
        state.calls[index] = { ...state.calls[index], ...action.payload };
      } else {
        // If call doesn't exist, add it
        state.calls.unshift(action.payload);
      }

      // Get the updated call (merged or new)
      const updatedCall = state.calls.find((c) => c.id === action.payload.id) || action.payload;

      const ongoingIndex = state.ongoingCalls.findIndex((c) => c.id === action.payload.id);
      if (updatedCall.status === "ongoing" && ongoingIndex === -1) {
        state.ongoingCalls.push(updatedCall);
      } else if (updatedCall.status !== "ongoing" && ongoingIndex !== -1) {
        state.ongoingCalls.splice(ongoingIndex, 1);
      } else if (updatedCall.status === "ongoing" && ongoingIndex !== -1) {
        state.ongoingCalls[ongoingIndex] = updatedCall;
      }

      // Clear active call if this call was active and is no longer ongoing
      if (state.activeCall?.id === action.payload.id) {
        if (updatedCall.status === "ongoing") {
          state.activeCall = updatedCall;
        } else {
          // Call ended, cancelled, rejected, etc. - clear the active call session
          state.activeCall = null;
          state.activeCallRoom = null;
          state.activeCallToken = null;
        }
      }
    },
    setActiveCallSession: (
      state,
      action: PayloadAction<{
        call: Call | null;
        room?: { url: string; name?: string } | null;
        token?: string | null;
      }>
    ) => {
      state.activeCall = action.payload.call;
      state.activeCallRoom = action.payload.room ?? null;
      state.activeCallToken = action.payload.token ?? null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const { setCalls, addCall, updateCall, setActiveCallSession, setLoading, setError } =
  callsSlice.actions;

export const selectAllCalls = (state: RootState) => state.calls.calls;
export const selectActiveCall = (state: RootState) => state.calls.activeCall;
export const selectActiveCallRoom = (state: RootState) => state.calls.activeCallRoom;
export const selectActiveCallToken = (state: RootState) => state.calls.activeCallToken;
export const selectOngoingCalls = (state: RootState) => state.calls.ongoingCalls;
export const selectCallsLoading = (state: RootState) => state.calls.isLoading;

export default callsSlice.reducer;
